import {
  forwardRef,
  useId,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';
import { cn } from '@shared/lib/cn';

/**
 * Floating-label text input per the design spec:
 *   - Resting state shows label inside the input at body size.
 *   - On focus or when filled, the label rises and shrinks to a small-caps
 *     eyebrow above the field.
 *   - Hairline bottom border by default; full hairline border + primary tint
 *     on focus.
 *   - Error message renders below at caption size in danger color and
 *     replaces helper text when present.
 *
 * Uncontrolled-friendly: works with react-hook-form's register() out of the
 * box. Pass label as a prop; do not render <label> separately.
 */

export interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  helper?: string;
  error?: string;
  leadingIcon?: ReactNode;
  trailingSlot?: ReactNode;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, helper, error, leadingIcon, trailingSlot, className, id, value, defaultValue, onFocus, onBlur, onChange, ...props },
  ref,
) {
  const reactId = useId();
  const fieldId = id ?? reactId;
  const [focused, setFocused] = useState(false);
  const [filled, setFilled] = useState(() => {
    if (value !== undefined && value !== '') return true;
    if (defaultValue !== undefined && defaultValue !== '') return true;
    return false;
  });

  const isFloating = focused || filled;
  const hasError = !!error;

  return (
    <div className={cn('relative w-full', className)}>
      <div
        className={cn(
          'relative flex items-end h-14 rounded-md transition-colors duration-fast ease-smooth bg-card',
          'border border-transparent',
          'border-b border-b-hairline',
          focused && !hasError && 'border-primary-500 border-b-primary-500',
          hasError && 'border-danger border-b-danger',
        )}
      >
        {leadingIcon ? (
          <div
            className="flex self-stretch items-center justify-center pl-4 pr-2 text-ink-muted"
            aria-hidden
          >
            {leadingIcon}
          </div>
        ) : null}

        <label
          htmlFor={fieldId}
          className={cn(
            'pointer-events-none absolute left-4 transition-all duration-fast ease-smooth select-none',
            leadingIcon ? 'left-12' : 'left-4',
            isFloating
              ? 'top-1.5 text-caption uppercase tracking-[0.08em] text-ink-muted'
              : 'top-1/2 -translate-y-1/2 text-body text-ink-secondary',
            hasError && 'text-danger',
          )}
        >
          {label}
        </label>

        <input
          ref={ref}
          id={fieldId}
          value={value}
          defaultValue={defaultValue}
          className={cn(
            'peer w-full bg-transparent px-4 pt-5 pb-2 text-body text-ink outline-none',
            leadingIcon && 'pl-2',
          )}
          onFocus={(event) => { setFocused(true); onFocus?.(event); }}
          onBlur={(event) => {
            setFocused(false);
            setFilled(event.target.value !== '');
            onBlur?.(event);
          }}
          onChange={(event) => {
            setFilled(event.target.value !== '');
            onChange?.(event);
          }}
          aria-invalid={hasError || undefined}
          aria-describedby={helper || error ? `${fieldId}-help` : undefined}
          {...props}
        />

        {trailingSlot ? (
          <div className="flex self-stretch items-center pr-3">{trailingSlot}</div>
        ) : null}
      </div>

      {(error || helper) ? (
        <p
          id={`${fieldId}-help`}
          className={cn(
            'mt-1.5 text-caption',
            hasError ? 'text-danger' : 'text-ink-muted',
          )}
        >
          {error ?? helper}
        </p>
      ) : null}
    </div>
  );
});
